"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import { useLocale, useRuntimeNavigate } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";

/**
 * Submit-capable site-search input rendered inside the home AudienceTools
 * widget. Pulled into a small client component because the surrounding
 * widget is a server component and only this input needs interactivity.
 *
 * Behavior is intentionally hardcoded: Enter or button click navigates to
 * `/search?q=<encoded query>` (or to the bare `/search` landing when the
 * input is empty — the search icon is always actionable). The site has
 * exactly one search route and one sensible submit behavior.
 *
 * Suggestions: on focus + on every keystroke (300ms debounce) the input
 * pings `/api/search/keywords` and shows up to 6 matching phrases — one
 * row per line, matched substring bolded. Same data source / UX as the
 * header search so users see consistent behaviour across both entry
 * points.
 *
 * The CMS owns the placeholder text and the surrounding label; everything
 * else (submit URL, suggestion source, debouncing) is fixed at this layer
 * because no second consumer needs to vary it.
 */
export function AudienceToolsSearchForm({ placeholder }: { placeholder: string }) {
	const [query, setQuery] = useState("");
	const [focused, setFocused] = useState(false);
	const [suggestions, setSuggestions] = useState<string[]>([]);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const abortRef = useRef<AbortController | null>(null);
	const cacheRef = useRef<Record<string, string[]>>({});
	const navigate = useRuntimeNavigate();
	const locale = useLocale();

	const submit = (value: string) => {
		const trimmed = value.trim();
		navigate(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : "/search");
	};

	const fetchSuggestions = async (q: string) => {
		abortRef.current?.abort();
		const controller = new AbortController();
		abortRef.current = controller;
		try {
			const langParam = locale && locale !== "fr" ? `lang=${encodeURIComponent(locale)}` : "";
			const url = q
				? `/api/search/keywords?q=${encodeURIComponent(q)}${langParam ? `&${langParam}` : ""}`
				: `/api/search/keywords${langParam ? `?${langParam}` : ""}`;
			const res = await fetch(url, { signal: controller.signal });
			if (!res.ok) return;
			const payload = await res.json();
			if (!Array.isArray(payload?.keywords)) return;
			const list = payload.keywords.filter((k: unknown): k is string => typeof k === "string");
			cacheRef.current[q] = list;
			if (query.trim() === q) setSuggestions(list);
		} catch (error) {
			if ((error as Error).name !== "AbortError") setSuggestions([]);
		}
	};

	useEffect(() => {
		if (!focused) {
			if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
			abortRef.current?.abort();
			return;
		}
		const trimmed = query.trim();
		const cached = cacheRef.current[trimmed];
		if (cached) {
			setSuggestions(cached);
			return;
		}
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => void fetchSuggestions(trimmed), trimmed ? 300 : 0);
		return () => {
			if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
		};
	}, [focused, query]);

	const visible = useMemo(() => suggestions.slice(0, 6), [suggestions]);
	const trimmed = query.trim();
	const normalisedQuery = useMemo(() => normaliseForMatch(trimmed), [trimmed]);

	return (
		<form
			role="search"
			className="relative mt-3"
			onSubmit={(event) => {
				event.preventDefault();
				submit(query);
			}}
		>
			<label className="sr-only" htmlFor="site-search">
				{t(locale, "searchSite")}
			</label>
			<input
				id="site-search"
				type="search"
				className="h-11 w-full rounded-sm border-none bg-white px-4 pr-12 text-sm xl:text-base placeholder-slate-500 text-[#0F2849] outline-none ring-0"
				placeholder={placeholder}
				autoComplete="off"
				value={query}
				onChange={(e) => setQuery(e.target.value)}
				onFocus={() => setFocused(true)}
				// Delay blur so a click on a suggestion fires before the panel hides.
				onBlur={() => setTimeout(() => setFocused(false), 120)}
			/>
			<button
				type="submit"
				aria-label={t(locale, "search")}
				className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-full text-[#0095DB] hover:bg-[#0095DB]/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#0095DB]"
			>
				<Search className="h-5 w-5" />
			</button>
			{focused && visible.length > 0 && (
				<div
					role="listbox"
					className="absolute left-0 right-0 top-full z-40 mt-1 overflow-hidden rounded-sm border border-[#005CA9]/15 bg-white text-[#0F2849] shadow-lg"
				>
					<div className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-[#0F2849]/75">
						{trimmed ? "Suggestions" : "Recherches populaires"}
					</div>
					<ul className="py-1">
						{visible.map((keyword) => (
							<li key={keyword}>
								<button
									type="button"
									role="option"
									aria-selected={false}
									className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-[#0F2849] transition hover:bg-[#005CA9]/8 focus:bg-[#005CA9]/10 focus:outline-none"
									onMouseDown={(e) => e.preventDefault()}
									onClick={() => submit(keyword)}
								>
									<Search size={14} className="shrink-0 text-[#005CA9]/70" />
									<span className="truncate">{renderHighlighted(keyword, normalisedQuery)}</span>
								</button>
							</li>
						))}
					</ul>
				</div>
			)}
		</form>
	);
}

/** Strips diacritics + lowercases — matches the server scoring rule. */
function normaliseForMatch(value: string): string {
	return value.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}

/**
 * Wraps the matched substring in `<strong>` so the user sees why a
 * suggestion was offered. Walks the original label so accented
 * characters render untouched while positions are computed against
 * the normalised label.
 */
function renderHighlighted(label: string, normalisedQuery: string) {
	if (!normalisedQuery) return label;
	const normLabel = normaliseForMatch(label);
	const idx = normLabel.indexOf(normalisedQuery);
	if (idx < 0) return label;
	const end = idx + normalisedQuery.length;
	return (
		<>
			{label.slice(0, idx)}
			<strong className="font-semibold text-[#005CA9]">{label.slice(idx, end)}</strong>
			{label.slice(end)}
		</>
	);
}
